import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import ConfirmModal from "../ConfirmModal";
import { adminFetch, isManagerMode } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-orders.css";

/**
 * RAdminOrders — /admin/orders
 *
 * Redesigned Manage Orders screen. Visuals/interaction per
 * design-ref/admin/Orders.html (+ Orders.logic.js); functionality replicated
 * 1:1 from the legacy frontend/src/pages/AdminOrdersPage.js:
 *
 *   - GET  /api/admin/orders?limit&offset[&status][&search]   (20 per page)
 *   - GET  /api/admin/orders/count?[status][search]           (pagination)
 *   - GET  /api/admin/orders/{id}                             (view detail)
 *   - PUT  /api/admin/orders/{id} {status}                    (silent status
 *          change — "New Order" / "Shipped (No Notification)", no email)
 *   - PUT  /api/admin/orders/{id} {status:'shipped',
 *          tracking_number, shipping_carrier}                 (two-path ship
 *          flow — tracking modal → "Mark as Shipped & Send Email")
 *   - DELETE /api/admin/orders/{id}                           (confirm modal;
 *          hidden entirely in manager mode)
 *
 * Manager mode (localStorage.managerMode === "true"): AdminShell renders the
 * orders-only sidebar + Order Manager identity and logout returns to
 * /manager; this screen additionally hides the Delete action.
 * Auth: adminFetch redirects to /admin/login (or /manager) on missing/expired
 * token or 401 — auth errors are never surfaced as toasts (err.isAuthError).
 */

const ORDERS_PER_PAGE = 20;

/* Same status enum as the legacy page; chip variants per CONVENTIONS-admin. */
const ORDER_STATUSES = [
  { value: "new_order", label: "New Order", chip: "amber" },
  { value: "shipped", label: "Shipped", chip: "teal" },
  {
    value: "shipped_no_notification",
    label: "Shipped (No Notification)",
    chip: "green",
  },
];

const statusInfoOf = (status) =>
  ORDER_STATUSES.find((s) => s.value === status);
const statusLabelOf = (status) => statusInfoOf(status)?.label || status;
const statusChipOf = (status) => statusInfoOf(status)?.chip || "grey";

/* ---- formatters (copied from the legacy page) ---- */
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);

const formatDate = (dateString) =>
  // Convert UTC time to Adelaide/South Australia timezone
  new Date(dateString).toLocaleString("en-AU", {
    timeZone: "Australia/Adelaide",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const getCustomerName = (order) => {
  if (order.customer && order.customer.first_name) {
    return `${order.customer.first_name} ${order.customer.last_name || ""}`.trim();
  }
  return order.customer_name || "Customer";
};

const getCustomerEmail = (order) => {
  if (order.customer && order.customer.email) {
    return order.customer.email;
  }
  return order.customer_email || "";
};

const getOrderTotal = (order) => {
  // Handle both total and total_amount fields
  const total = order.total || order.total_amount || 0;
  return isNaN(total) ? 0 : total;
};

/* ---- inline SVGs copied from the design prototype ---- */
const RefreshIcon = (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="#2a2018"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 11a8 8 0 10-2 6" />
    <path d="M20 4v5h-5" />
  </svg>
);

const EyeIcon = (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="#54504a"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const TruckIcon = (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="#1bab88"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="6" width="14" height="10" rx="1" />
    <path d="M15 9h4l3 3v4h-7z" />
    <circle cx="6" cy="18" r="1.6" />
    <circle cx="18" cy="18" r="1.6" />
  </svg>
);

/**
 * AdminShell mounts the AdminToastProvider, so the hook can only run in a
 * child of the shell. This bridge exposes the toast api to the page (which
 * renders the shell itself) through a ref — toasts only fire from event
 * handlers/async callbacks, well after mount.
 */
const OrToastBridge = ({ toastRef }) => {
  toastRef.current = useAdminToast();
  return null;
};

const RAdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [trackingOrder, setTrackingOrder] = useState(null); // order being shipped
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("Australia Post");
  const [shipBusy, setShipBusy] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const navigate = useNavigate();
  const toastRef = useRef(null);

  // Store managers log in via /manager and only get the orders view
  const isManager = isManagerMode();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        limit: ORDERS_PER_PAGE.toString(),
        offset: ((currentPage - 1) * ORDERS_PER_PAGE).toString(),
      });
      if (statusFilter && statusFilter !== "all")
        params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);

      const response = await adminFetch(`/api/admin/orders?${params}`, {
        navigate,
      });
      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }
      const ordersData = await response.json();
      setOrders(ordersData);

      // Get total count for pagination
      const countParams = new URLSearchParams();
      if (statusFilter && statusFilter !== "all")
        countParams.append("status", statusFilter);
      if (searchTerm) countParams.append("search", searchTerm);

      const countResponse = await adminFetch(
        `/api/admin/orders/count?${countParams}`,
        { navigate }
      );
      if (countResponse.ok) {
        const countData = await countResponse.json();
        // Backend returns { total_orders: N }. (The legacy page read
        // countData.count, which was always undefined — pagination total was
        // silently broken. Read the real field, keep .count as a fallback,
        // and default to 0 so the count is never NaN/undefined.)
        setTotalOrders(countData.total_orders ?? countData.count ?? 0);
      }
    } catch (err) {
      if (!err.isAuthError) toastRef.current?.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchTerm, navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /* ---- status workflow (important asymmetry, same as legacy):
         → shipped opens the tracking modal; anything else saves silently ---- */
  const updateOrderStatus = async (order, newStatus) => {
    if (newStatus === order.status) return;

    // If changing to shipped status, show tracking modal
    if (newStatus === "shipped") {
      setTrackingOrder(order);
      return;
    }

    try {
      const response = await adminFetch(
        `/api/admin/orders/${order._id || order.id}`,
        {
          method: "PUT",
          body: JSON.stringify({ status: newStatus }),
          navigate,
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update order status");
      }
      fetchOrders();
    } catch (err) {
      if (!err.isAuthError) toastRef.current?.error(err.message);
    }
  };

  const closeTrackingModal = useCallback(() => {
    setTrackingOrder(null);
    setTrackingNumber("");
    setTrackingCarrier("Australia Post");
  }, []);

  const updateOrderWithTracking = async () => {
    if (!trackingNumber.trim()) {
      toastRef.current?.error("Please enter a tracking number");
      return;
    }

    try {
      setShipBusy(true);
      const response = await adminFetch(
        `/api/admin/orders/${trackingOrder._id || trackingOrder.id}`,
        {
          method: "PUT",
          body: JSON.stringify({
            status: "shipped",
            tracking_number: trackingNumber,
            shipping_carrier: trackingCarrier,
          }),
          navigate,
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update order with tracking information");
      }

      toastRef.current?.success("✓ Order marked as shipped — email sent");
      closeTrackingModal();
      fetchOrders();
    } catch (err) {
      if (!err.isAuthError) toastRef.current?.error(err.message);
    } finally {
      setShipBusy(false);
    }
  };

  const viewOrderDetail = async (orderId) => {
    try {
      const response = await adminFetch(`/api/admin/orders/${orderId}`, {
        navigate,
      });
      if (response.ok) {
        const orderData = await response.json();
        setOrderDetail(orderData);
        setShowOrderDetail(true);
      } else {
        throw new Error("Failed to fetch order details");
      }
    } catch (err) {
      if (!err.isAuthError) toastRef.current?.error(err.message);
    }
  };

  const confirmDelete = async () => {
    const order = deletingOrder;
    setDeletingOrder(null); // dialog dismisses on confirm (like window.confirm)
    if (!order) return;

    try {
      const response = await adminFetch(
        `/api/admin/orders/${order._id || order.id}`,
        { method: "DELETE", navigate }
      );
      if (!response.ok) {
        throw new Error("Failed to delete order");
      }
      // Refresh orders
      fetchOrders();
      toastRef.current?.success("Order deleted");
    } catch (err) {
      if (!err.isAuthError) toastRef.current?.error(err.message);
    }
  };

  /* Escape closes the ship / detail modals (ConfirmModal handles its own). */
  useEffect(() => {
    if (!trackingOrder && !showOrderDetail) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (trackingOrder) closeTrackingModal();
      else setShowOrderDetail(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [trackingOrder, showOrderDetail, closeTrackingModal]);

  const totalPages = Math.ceil(totalOrders / ORDERS_PER_PAGE);
  const windowSize = Math.min(5, totalPages);
  const windowStart = Math.max(
    1,
    Math.min(currentPage - 2, totalPages - windowSize + 1)
  );
  const pageNumbers = Array.from(
    { length: windowSize },
    (_, i) => windowStart + i
  );

  const detailEmail = orderDetail ? getCustomerEmail(orderDetail) : "";
  const detailPhone =
    (orderDetail && orderDetail.customer && orderDetail.customer.phone) ||
    "Not provided";

  return (
    <AdminShell
      active="orders"
      pageClass="pg-admin-orders"
      eyebrow="Commerce"
      title="Manage Orders"
      subtitle={`${totalOrders.toLocaleString("en-AU")} total orders`}
      actions={
        <button
          type="button"
          className={"adm-iconbtn" + (loading ? " or-refresh--loading" : "")}
          onClick={fetchOrders}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh orders"
        >
          {RefreshIcon}
        </button>
      }
    >
      <OrToastBridge toastRef={toastRef} />
      <div className="pg-admin-orders">
        {/* ---- filters ---- */}
        <div className="adm-card adm-card--flush">
          <div className="adm-filterbar">
            <input
              className="adm-input adm-input--search"
              type="text"
              placeholder="Search by number, customer, or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search orders"
            />
            <select
              className="adm-select adm-select--filter"
              value={statusFilter || "all"}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              {ORDER_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="adm-btn adm-btn--ghost"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setCurrentPage(1);
              }}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="adm-gap" />

        {/* ---- orders table ---- */}
        <div className="adm-card">
          {loading ? (
            <div className="adm-loading">
              <div className="adm-spinner" />
              Loading orders…
            </div>
          ) : orders.length === 0 ? (
            <div className="adm-empty">
              <div className="adm-empty-icon">📦</div>
              <div className="adm-empty-title">No orders found</div>
              <div className="adm-empty-sub">
                {searchTerm || statusFilter
                  ? "Try adjusting your filters"
                  : "Orders will appear here once customers make purchases"}
              </div>
            </div>
          ) : (
            <div className="adm-tablewrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const itemCount = order.items?.length || 0;
                    const known = !!statusInfoOf(order.status);
                    return (
                      <tr key={order._id || order.id}>
                        <td>
                          <div className="adm-td-strong">
                            #{order.order_number}
                          </div>
                          <div className="adm-td-sub">
                            {itemCount} {itemCount === 1 ? "item" : "items"}
                          </div>
                        </td>
                        <td>
                          <div className="adm-td-em">
                            {getCustomerName(order)}
                          </div>
                          <div className="adm-td-sub adm-td-sub--email">
                            {getCustomerEmail(order)}
                          </div>
                        </td>
                        <td>
                          {/* status chip stays inline-editable (legacy Select) */}
                          <select
                            className={`or-status or-status--${statusChipOf(order.status)}`}
                            value={order.status || ""}
                            onChange={(e) =>
                              updateOrderStatus(order, e.target.value)
                            }
                            title="Change status"
                            aria-label={`Order status: ${statusLabelOf(order.status)}`}
                          >
                            {!known && (
                              <option value={order.status || ""} disabled>
                                {order.status || "Unknown"}
                              </option>
                            )}
                            {ORDER_STATUSES.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="adm-td-total">
                          {formatCurrency(getOrderTotal(order))}
                        </td>
                        <td className="adm-td-mute">
                          {formatDate(order.created_at)}
                        </td>
                        <td>
                          <div className="adm-row-actions">
                            <button
                              type="button"
                              className="adm-iconbtn adm-iconbtn--sm"
                              title="View"
                              aria-label="View order"
                              onClick={() =>
                                viewOrderDetail(order._id || order.id)
                              }
                            >
                              {EyeIcon}
                            </button>
                            {order.status === "shipped" &&
                              order.tracking_number && (
                                <button
                                  type="button"
                                  className="adm-iconbtn adm-iconbtn--sm adm-iconbtn--teal"
                                  title="Track"
                                  aria-label="Track shipment"
                                  onClick={() =>
                                    window.open(
                                      `https://auspost.com.au/mypost/track/#/details/${order.tracking_number}`,
                                      "_blank"
                                    )
                                  }
                                >
                                  {TruckIcon}
                                </button>
                              )}
                            {!isManager && (
                              <button
                                type="button"
                                className="adm-iconbtn adm-iconbtn--sm adm-iconbtn--danger"
                                title="Delete"
                                aria-label="Delete order"
                                onClick={() => setDeletingOrder(order)}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ---- pagination (legacy: only when more than one page) ---- */}
          {totalPages > 1 && (
            <div className="adm-pagination">
              <span className="adm-page-info">
                Showing {(currentPage - 1) * ORDERS_PER_PAGE + 1}–
                {Math.min(currentPage * ORDERS_PER_PAGE, totalOrders)} of{" "}
                {totalOrders.toLocaleString("en-AU")}
              </span>
              <div className="adm-pages">
                <button
                  type="button"
                  className="adm-pagebtn"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {pageNumbers.map((pageNum) => (
                  <button
                    type="button"
                    key={pageNum}
                    className={
                      "adm-pagebtn" +
                      (pageNum === currentPage ? " is-active" : "")
                    }
                    onClick={() => setCurrentPage(pageNum)}
                    aria-current={pageNum === currentPage ? "page" : undefined}
                  >
                    {pageNum}
                  </button>
                ))}
                <button
                  type="button"
                  className="adm-pagebtn"
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ---- tracking modal (ship + email path) ---- */}
        {trackingOrder && (
          <div className="adm-modal-overlay" onClick={closeTrackingModal}>
            <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="adm-modal-title">
                Mark order #{trackingOrder.order_number} as Shipped
              </div>
              <div className="adm-note or-ship-note">
                ✉ A shipping notification email will be sent to the customer.
              </div>
              <label className="adm-field">
                <span className="adm-label">Shipping Carrier</span>
                <select
                  className="adm-select"
                  value={trackingCarrier}
                  onChange={(e) => setTrackingCarrier(e.target.value)}
                >
                  <option value="Australia Post">Australia Post</option>
                  <option value="Toll">Toll</option>
                  <option value="StarTrack">StarTrack</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              <label className="adm-field">
                <span className="adm-label">Tracking Number *</span>
                <input
                  className="adm-input"
                  type="text"
                  placeholder="AP0000123456789"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
              </label>
              <div className="adm-modal-actions">
                <button
                  type="button"
                  className="adm-btn adm-btn--soft"
                  onClick={closeTrackingModal}
                  disabled={shipBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="adm-btn adm-btn--wide"
                  onClick={updateOrderWithTracking}
                  disabled={!trackingNumber.trim() || shipBusy}
                >
                  Mark as Shipped &amp; Send Email
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---- order detail modal (read-only) ---- */}
        {showOrderDetail && orderDetail && (
          <div
            className="adm-modal-overlay"
            onClick={() => setShowOrderDetail(false)}
          >
            <div
              className="adm-modal adm-modal--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="adm-modal-head">
                <div className="adm-modal-title">
                  Order #{orderDetail.order_number}
                </div>
                <button
                  type="button"
                  className="adm-modal-close"
                  onClick={() => setShowOrderDetail(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="adm-note">
                <div className="adm-note-eyebrow">Customer</div>
                <div className="adm-note-strong">
                  {getCustomerName(orderDetail)}
                </div>
                <div className="or-cust-sub">
                  {[detailEmail, detailPhone].filter(Boolean).join(" · ")}
                </div>
              </div>

              <div className="adm-note adm-note--teal">
                <div className="adm-note-eyebrow">Order info</div>
                <div className="or-infogrid">
                  <span>
                    Status: <strong>{statusLabelOf(orderDetail.status)}</strong>
                  </span>
                  <span>
                    Total:{" "}
                    <strong>{formatCurrency(getOrderTotal(orderDetail))}</strong>
                  </span>
                  <span>Date: {formatDate(orderDetail.created_at)}</span>
                  <span>
                    Payment: {orderDetail.payment_method || "Credit Card"}
                  </span>
                </div>
              </div>

              <div className="or-items">
                <div className="or-items-eyebrow">Items</div>
                {orderDetail.items?.map((item, index) => {
                  const qty = item.quantity || 0;
                  const price = Number(item.price) || 0;
                  return (
                    <div className="or-item" key={index}>
                      <span>
                        {item.product?.name || "Product"}{" "}
                        {item.product?.sku && (
                          <span className="or-item-sku">
                            ({item.product.sku})
                          </span>
                        )}{" "}
                        × {qty}
                      </span>
                      <span className="or-item-right">
                        <strong>{formatCurrency(qty * price)}</strong>
                        <span className="or-item-each">
                          {formatCurrency(price)} each
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>

              {orderDetail.shipping_address && (
                <div className="adm-note adm-note--purple">
                  <div className="adm-note-eyebrow">Shipping address</div>
                  <div className="adm-note-body">
                    {orderDetail.shipping_address.first_name}{" "}
                    {orderDetail.shipping_address.last_name}
                    <br />
                    {orderDetail.shipping_address.address_line_1}
                    <br />
                    {orderDetail.shipping_address.address_line_2 && (
                      <>
                        {orderDetail.shipping_address.address_line_2}
                        <br />
                      </>
                    )}
                    {orderDetail.shipping_address.city},{" "}
                    {orderDetail.shipping_address.state}{" "}
                    {orderDetail.shipping_address.postal_code}
                    <br />
                    {orderDetail.shipping_address.country}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- delete confirm (replaces legacy window.confirm) ---- */}
        <ConfirmModal
          open={!!deletingOrder}
          title="Delete this order?"
          body="This will permanently remove the order. This cannot be undone."
          confirmLabel="Delete Order"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setDeletingOrder(null)}
        />
      </div>
    </AdminShell>
  );
};

export default RAdminOrders;
